import { type ReportResponse } from '~/lib/Api.types';

import {
    type CellRendererParams,
    type ColumnDefinition,
    type ColumnDefinitionField,
} from '~/components/Grid';
import { FORM_NAME_COLUMN_KEY } from './FormsGrid';
import DateTime from '~/lib/NewDateTime';
import { type TemplateIndex } from '../../ReportsIndexPage/Report.types';
import {
    datesComparator,
    tweakColumnIfPremadeTemplateRelated,
} from './Grid.utils';
import {
    KeysThatWillBeHidden,
    KeysRelativeToDates,
    KeysStrictlyFilteredAsFreeText,
    FilterDateFormatToUse,
    SortDateFormatToUse,
    KeysRelativeToMoney,
    KeySuffixesRelativeToMoney,
} from './FormsGrid.constants';

import { REGISTERED_GRID_COMPONENTS } from './Grid.constants';

export function removeBlacklistFields<ReportObject>(
    reportResponse: ReportResponse<ReportObject>,
    blacklist: Array<keyof ReportObject>,
): ReportResponse<ReportObject> {
    if (
        !blacklist ||
        blacklist?.length === 0 ||
        !reportResponse ||
        reportResponse?.keys?.length === 0
    ) {
        return reportResponse;
    }

    // Filter the keys
    const filteredKeys = reportResponse?.keys?.filter(
        key => !blacklist.includes(key as keyof ReportObject),
    );

    // Filter the data
    const filteredData = reportResponse.data.map((item: ReportObject) =>
        Object.entries(item).reduce((acc: ReportObject, [key, value]) => {
            if (!blacklist.includes(key as keyof ReportObject)) {
                // Type assertion to ensure value is of the correct type
                acc[key as keyof ReportObject] =
                    value as ReportObject[keyof ReportObject];
            }
            return acc; // Return the accumulated object
        }, {} as ReportObject),
    );

    return {
        keys: filteredKeys,
        data: filteredData,
    };
}

export function dateColumnFormatter<ReportDataObj, ReportKey>(
    formColumn: ColumnDefinition<ReportDataObj>,
    formKey: ReportKey,
): ColumnDefinition<ReportDataObj> {
    // Dates
    formColumn.valueFormatter = ({ value }) => {
        if (value == null) {
            return value;
        }

        return typeof value === 'string' ? value : String(value);
    };
    formColumn.filter = 'agDateColumnFilter';
    formColumn.filterValueGetter = params => {
        if (
            params?.data &&
            // @ts-expect-error typescript won't let us use complex types like ReportKey as keys unfortunately
            params.data[formKey]
        ) {
            const datetimeString = params.data[formKey];
            const datetime = DateTime.fromRFC1123Format(
                datetimeString as string,
            ).startOf('day'); // set to start of day so that equals filter works (otherwise it also checks time and fails to work)

            if (datetime.isValid()) {
                return datetime.format(FilterDateFormatToUse);
            }
        }

        return '';
    };
    formColumn.filterParams = {
        // comparator for filtering
        comparator: (date1: string, date2: string) =>
            datesComparator(
                date1,
                date2,
                'day',
                FilterDateFormatToUse,
                FilterDateFormatToUse,
            ),
        inRangeInclusive: true,
        ...formColumn.filterParams,
    };

    // comparator for sorting
    formColumn.comparator = (date1: string, date2: string) => {
        let parsedDate1 = DateTime.fromRFC1123Format(date1 || '1980-01-01 00:00:00');
        let parsedDate2 = DateTime.fromRFC1123Format(date2 || '1980-01-01 00:00:00');

        if (!parsedDate1?.isValid()) {
            parsedDate1 = new Date(date1);
        } else {
            parsedDate1 = parsedDate1?.toJsDate()
        }
        if (!parsedDate2?.isValid()) {
            parsedDate2 = new Date(date2);
        } else {
            parsedDate2 = parsedDate2?.toJsDate()
        }

        if (isNaN(parsedDate1)) {
            parsedDate1 = new Date('1980-01-01 00:00:00');
        }
        if (isNaN(parsedDate2)) {
            parsedDate2 = new Date('1980-01-01 00:00:00');
        }

        return datesComparator(
            parsedDate1,
            parsedDate2,
            'minute',
            SortDateFormatToUse,
            SortDateFormatToUse,
        );
    };
    return formColumn;
}

export function textColumnFormatter<ReportDataObj>(
    formColumn: ColumnDefinition<ReportDataObj>,
    // formKey: ReportKey,
): ColumnDefinition<ReportDataObj> {
    formColumn.filter = 'agTextColumnFilter';
    return formColumn;
}

export function createClickableColumn<ColumnData = { id?: number }>(
    onClick: (event: { id: any }) => void,
    idExtractor?: (params: CellRendererParams) => number | undefined,
    fallbackValue?: string,
): ColumnDefinition<ColumnData> {
    return {
        headerName: 'Details',
        suppressSizeToFit: true, // Optional: Prevent resizing
        pinned: 'left',
        cellRendererSelector: (params: CellRendererParams<ColumnData>) => {
            const id =
                idExtractor && params ? idExtractor(params) : params.data?.id;

            return {
                // else use our custom renderer
                component: REGISTERED_GRID_COMPONENTS.CLICKABLE_COMPONENT,
                params: { id, onClick, fallbackValue },
            };
        },
    };
}

export function createColumnsBasedOnDataKeys<
    ReportData extends { keys: Array<string>; data: Array<unknown> },
>(keys: ReportData['keys'], templateSelected?: TemplateIndex) {
    type ReportKey = ReportData['keys'][0];
    type ReportDataObj = ReportData['data'][0];
    return (
        keys?.map?.((formKey: ReportKey) => {
            let formColumn: ColumnDefinition<ReportDataObj> = {
                field: formKey as ColumnDefinitionField<ReportDataObj>,
                filter: true,
                hide: !!KeysThatWillBeHidden.includes(
                    (formKey || '').toLowerCase(),
                ),
                enableRowGroup: true,
                enableValue: true,
            };

            if (KeysRelativeToDates.includes(formKey.toLowerCase())) {
                formColumn = dateColumnFormatter<ReportDataObj, ReportKey>(
                    formColumn,
                    formKey,
                );
            } else if (
                KeysStrictlyFilteredAsFreeText.includes(formKey.toLowerCase())
            ) {
                // free text
                formColumn = textColumnFormatter(formColumn);
            } else if (KeysRelativeToMoney.includes(formKey.toLowerCase()) || KeySuffixesRelativeToMoney.some(suffix => formKey.toLowerCase().endsWith(suffix))) {
                formColumn.type = 'currency';
                formColumn.filter = 'agNumberColumnFilter';
                formColumn.valueGetter = params => {
                    const raw = params?.data?.[formKey];
                    if (raw == null || raw === '') return null;
                    if (typeof raw === 'number') return raw;
                    const value = String(raw).trim();
                    const negative = value.startsWith('(') && value.endsWith(')');
                    const digits = value.replace(/[^\d,.-]/g, '').replace(/[()]/g, '');
                    const decimalIsComma =
                        digits.lastIndexOf(',') > digits.lastIndexOf('.');
                    const normalized = decimalIsComma
                        ? digits.replace(/\./g, '').replace(',', '.')
                        : digits.replace(/,/g, '');
                    const num = Number(normalized);
                    return Number.isNaN(num) ? null : (negative ? -num : num);
                };
            }
            if (formKey === FORM_NAME_COLUMN_KEY) {
                // for form name columns
                formColumn.filterParams = {
                    maxNumConditions: 999, // enable multiple filters so that users can select multiple forms in the checkboc
                    ...formColumn.filterParams,
                };
            }

            return tweakColumnIfPremadeTemplateRelated(
                formKey,
                formColumn,
                templateSelected,
            );
        }) || []
    );
}

type VagueColumnDefinitionArray = Array<
    ColumnDefinition<{
        id?: number;
    }>
>;
export function appendClickableColumns(
    config: Array<{
        condition: boolean;
        idExtractor: (params: CellRendererParams) => any;
        fallbackValue?: string;
    }>,
    onClick?: (event: { id: number }) => void,
): VagueColumnDefinitionArray {
    if (!config || !onClick) {
        return [];
    }
    return (
        (config
            .map(({ condition, idExtractor, fallbackValue }) => {
                if (condition) {
                    return createClickableColumn(
                        onClick,
                        idExtractor,
                        fallbackValue,
                    );
                }
                return null;
            })
            .filter(Boolean) as VagueColumnDefinitionArray) ?? []
    );
}
